import type { LightingCapability } from './lighting'
import type { KeyScanRate, ReportRate, SensorMode } from './mousePerfCfg'

/**
 * 设备接口类型定义
 * 用于区分复合设备中的不同功能接口
 */
export type DeviceInterfaceType =
  | 'vendor' // 厂商自定义
  | 'keyboard' // 标准键盘接口
  | 'mouse' // 标准鼠标接口
  | 'base' // 底座接口
  | 'other' // 其他接口
// TODO：后面加

export interface ColorSeriesItem {
  name: string
  value: string | string[]
  /** 颜色标识 key，用于拼接资源路径（如 assetsDir + 'func/' + key + '.png'） */
  key: string
}

export interface UI {
  /** 资源文件夹路径 */
  assetsDir: string
  /** 颜色配置 */
  colors: {
    series: ColorSeriesItem[]
    current: string
  }
  /** 小图标 */
  icon: string
  /** 是否支持设备概览 */
  overview: boolean
}

export interface DeviceManifest {
  /** 打包渠道，支持同时打进多个发行包；不填视为全渠道支持 */
  channel?: string[]

  /** 型号名称 (e.g. "VT9", "V900") */
  model: string

  /**
   * 主控型号
   * 此字段主要用于获取设备的后台保存的文件数据（版本、固件等）
   * 可能会有主控不同但要视为同一个model型号的情况，所以要加这个区分具体主控
   * 界面端应当将model相同的视为同一设备只显示一个
   * 正常来说可以和model保持一致
   */
  mainControl: string

  /**
   * 无线型号
   */
  dongleModel?: string

  type: DeviceInterfaceType // 强制指定接口用途

  /** 固件版本/Manifest版本  */
  version?: string

  /** 硬件识别信息 */
  hardware: {
    vendorId: number
    productId: number | number[] // 支持多个 PID (如有线/无线模式)
    /** 仅用于接收器配对的额外 PID，沿用 vendorId，不参与设备身份匹配。 */
    pairingProductIds?: number[]
    currentPid: number // 当前 PID, 默认可以填第一个有线的
    transport: 'HID' | 'BLE'

    /**
     * 接口过滤器列表
     * 定义需要匹配的 Usage Page / Usage 组合
     * 通常用于定位 Vendor Specific 接口
     */
    filters?: {
      usagePage: number
      usage: number
      description?: string // 可选描述
      type?: usageType
      /**
       * 可选：该接口使用与 APP 不同的 VID/PID（如 GCOME Bootloader 重枚举身份）。
       * 未填时沿用 hardware.vendorId / productId。
       */
      vendorId?: number
      productId?: number | number[]
      /**
       * 可选：系统选择器中常见的产品名示意（弹窗蓝色文案，不含 VID/PID）。
       * 例：GCOME Boot「Volt* Upgrader」。
       */
      productName?: string
    }[]
  }

  /**
   * 驱动策略，协议
   * 告诉 Factory 应该用哪个类来实例化这个设备
   */
  driver: string // e.g. "VT_nrf54L", "V_General"
  /** 型号级 driver 私有选项；具体键和值由对应 driver 校验，业务层不直接读取。 */
  driverOptions?: Record<string, unknown>
  MCU?: string // 单片机型号 (e.g. "N54L15", "N54H20")
  sensor?: 'PAW3398' | 'PAW3950U' | 'PAW3955U' | 'PAW3311' // 传感器型号 (e.g. "PAW3398/3950/3955")
  microSwitch?: 'optical' | 'mechanical' // 按键类型 光微动 | 机械微动
  encoder?: 'optical' | 'mechanical' // 编码器类型 光编码 | 机械编码
  battery?: number // 电池容量
  baseDisplay?: 'lighting' | 'screen' // 底座屏幕类型，没有则是无屏 纯灯效 | 可变显示屏
  /** 设备 UI 信息 */
  ui: UI

  capabilities: Capability[]

  layout: {
    image?: string // 键盘底图
    top?: number // 键盘按键和底图的间距
    left?: number
    bottom?: number
    right?: number
    unit?: number // 键盘按键按钮大小单位
    keys: LayoutKey[][]
  }
}

export interface DeviceInfo {
  vendorId: number
  productId: number
  productName?: string
}

/**
 * 设备能力声明。
 *
 * 这是一个判别联合，`type` 决定 `config` 的形状。新增能力必须在此登记一个成员，
 * 不要引入 `type: string` / `config: Record<string, unknown>` 之类的兜底成员：
 * 那会让整个联合退化成任意对象，manifest 里写错 type 名或 config 字段名都不再报错。
 */
export type Capability =
  | LightingCapability
  | DpiCapability
  | ConfigCapability
  | ParametersCapability
  | BaseScreenCapability
  | BaseLightingCapability
  | BaseChannelCapability
  | BaseChildDeviceCapability
  | ItineraryCapability
  | KeyFeaturesCapability
  | KeyboardPerfCapability
  | MouseKeysCapability
  | KeysCapability
  | InputReportCapability
  | MacroCapability
  | MouseRTCapability
  | MousePerfCapability
  | PairingCapability

/** 已登记的能力标识，从 Capability 反推以避免与联合脱节。 */
export type CapabilityType = Capability['type']

/** 鼠标改键及宏编辑器的设备级能力声明。 */
export interface MouseKeysCapability {
  type: 'mouseKeys'
  config: {
    /** 基础改键列表中需要隐藏的功能。 */
    hiddenBasicKeys?: string[]
    /** 宏坐标动作的设备可保存范围；未配置时由页面使用通用默认范围。 */
    macroCoordinateRange?: {
      min: number
      max: number
    }
  }
}

/**
 * 固件回读中需要在 UI 隐藏或改描述的键。
 * keywords 与 type/code 二选一，写了 keywords 就按 keywords 匹配。
 */
export interface KeyDisplayOverride {
  keywords?: string
  type?: number
  code?: number
  /** 命中后不展示为已改键 */
  hidden?: boolean
  labelKey?: string
  label?: string
}

/**
 * 指定协议中主动上报的特别区分处理。
 * 例如 Telink `V700`、Nordic 鼠标网吧 `cybercafe`。
 */
export interface InputReportCapability {
  type: 'input-report'
  config: {
    type: string
  }
}

/**
 * Fn 层出厂映射：物理键 keywords → 出厂功能 keywords。
 * 只需列出「出厂 ≠ 物理键自身」的键。
 */
export type FactoryKeyMap = Record<string, string>

/**
 * 键盘改键能力：出厂 Fn 层映射 + 可用多媒体改键白名单 + 展示改写规则。
 * factoryKeys.win / factoryKeys.mac：按 Win 层 / Mac 层分别配置 Fn 出厂键
 * mediaKeys：多媒体 Tab 可展示/改键的 keywords 列表（顺序即展示顺序）
 * displayOverrides：固件回读中需要隐藏或改描述的键
 */
export interface KeysCapability {
  type: 'keys'
  config: {
    factoryKeys: {
      win: FactoryKeyMap
      mac: FactoryKeyMap
    }
    mediaKeys: string[]
    displayOverrides?: KeyDisplayOverride[]
  }
}

export type LayoutKey = {
  key: number // 按键唯一值（固件 LED/矩阵索引）
  keywords: string
  alignLeft?: boolean // 鼠标属性，按键按钮位于左侧还是右侧
  url?: string // 鼠标属性，按键按钮底图
  x?: number
  y?: number
  w?: number
  h?: number
  /**
   * 长键（空格等）的额外 LED 灯珠索引。
   * 与 key 主索引一起参与自定义灯效写入/读取，如空格 = key 38 + ledIndexes [82,83,84,85]。
   */
  ledIndexes?: number[]
}

export interface ConfigCapability {
  type: 'config'
  config: {
    minNameLen?: number // 最小名称长度，不填则默认为 1
    maxNameLen: number // 最大名称长度
    maxConfig?: number // 最大配置数量，不填则以设备实际地址数量为准
    configSlots?: ConfigSlot[] // 配置槽位，不填则以设备实际地址为准
    adminFactory?: boolean // 是否支持管理员出厂模式，即设备硬件能力是否支持管理员调整出厂设置
  }
}

export interface ConfigSlot {
  id: number // 配置槽位ID
  roles: Role[] // 角色权限
}

export const RoleId = {
  superAdmin: 1, // 超级管理员
  admin: 2, // 管理员
  user: 3, // 普通用户
} as const

export type Role = (typeof RoleId)[keyof typeof RoleId]

// 底座灯带功能
export interface BaseLightingCapability {
  type: 'baseLighting'
  config: {
    enable: boolean
    modes: {
      id: number
      i18nKey: string
      type: string
      ableChange: string[]
      tip?: string
    }[]
    targets: string[]
  }
}

/** 底座固件屏显页面使用的预设资源。 */
export interface PagePreset {
  /** 相对于 manifest.ui.assetsDir 的资源路径。 */
  asset: string
  /** 固件定义的全局样式 index。 */
  style: number
}

export type BaseScreenSystemPageKind = 'home' | 'mouse-rt'

/** 固件内置且不允许作为用户图片槽位处理的屏显页面。 */
export interface BaseScreenSystemPage {
  /** 固件物理槽位。 */
  slot: number
  /** 系统页面语义。 */
  kind: BaseScreenSystemPageKind
  /** 当前页面拥有或允许使用的固件预设。 */
  presetScreens: PagePreset[]
}

// 底座屏幕功能
export interface BaseScreenCapability {
  type: 'baseScreen'
  config: {
    enable: boolean
    width: number
    height: number
    /** 屏幕类型；无屏底座不声明（如 P5 PLUS / P6 PLUS）。 */
    screenType?: 'logo' | 'screen'
    screenUsagePage: number
    screenUsage: number
    /** 屏显物理槽位总数，有效 slot 范围为 0 ~ maxScreenSlots - 1。 */
    maxScreenSlots?: number
    /** 固件内置页面；未声明的槽位视为用户自定义页面。 */
    systemPages?: BaseScreenSystemPage[]
  }
}

// 底座无线信道
export interface BaseChannelCapability {
  type: 'baseChannel'
  config: {
    enable: boolean
    min: number
    max: number
  }
}

// 底座是否管理子设备（挂在底座下的鼠标）
export interface BaseChildDeviceCapability {
  type: 'baseChildDevice'
  config: {
    enable: boolean
  }
}

/**
 * 对码流程的 UI 差异声明。
 * 注意：SDK 侧识别对码通道走的是 `hardware.filters[].type === 'pairing'`，与本能力无关。
 */
export interface PairingCapability {
  type: 'pairing'
  config: {
    /** 隐藏对码引导第二步 */
    hideStep2?: boolean
    /** 隐藏对码说明文案 */
    hideInstruction?: boolean
  }
}

// 鼠标参数功能
export type KeyDebounceDisplayPage = '' | 'rt'

export interface ParametersCapability {
  type: 'parameters'
  config: {
    // 静默高度
    liftoffHeight: [
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
      number,
    ]
    /**
     * 按键防抖模块的展示位置。
     * - 空值或未配置：鼠标参数页
     * - `rt`：鼠标按键行程页的「按键 RT 行程」卡片
     */
    keyDebounce?: {
      displayPage?: KeyDebounceDisplayPage
    }
    /** 无线休眠时间范围（分钟） */
    sleepTimeRange?: { min: number; max: number }
  }
}

// 鼠标dpi功能
export interface DpiCapability {
  type: 'dpi'
  config: {
    min: number // dpi最小值
    max: number // dpi最大值
    step: number | DpiStep[]
  }
}

/** 鼠标 RT 页面单项数值约束，由具体产品 manifest 提供。 */
export interface MouseRTValueConfig {
  min: number
  max: number
  default: number
}

export interface MouseRTCapability {
  type: 'mouse-rt'
  config: {
    enable: boolean
    /**
     * RT 预设上传和查询使用的完整配置类型。
     * 相同值的产品共用用户预设，例如 `VT2:mouse-rt`。
     */
    configType: string
    /** 按键触发行程级别。 */
    triggerTravel: MouseRTValueConfig
    /** 快速触发（RT）级别。 */
    rtTriggerLevel: MouseRTValueConfig
    /** 点击振动强度，0 表示关闭。 */
    vibration: MouseRTValueConfig
    /** 振动反馈所模拟的微动类型：0=机械、1=光、2=电磁。 */
    vibrationFeedbackType: MouseRTValueConfig
    /** 点击音效类型，0 表示关闭。 */
    sound: MouseRTValueConfig
    /** 点击音效音量，0 表示静音。 */
    volume: MouseRTValueConfig
  }
}

/**
 * 传感器模式档位的文案预设名。
 * 协议值与档位含义不是固定对应关系（PAW3311 把协议值 5 用作「超核」而非「狂爆竞技」），
 * 所以由 manifest 指定每个协议值走哪套文案。
 */
export type SensorModePreset =
  | 'frenzy'
  | 'competitive'
  | 'hyperCore'
  | 'firepower'
  | 'balanced'
  | 'office'

/** 传感器模式档位声明：value 是协议值，preset 决定界面文案。 */
export interface SensorModeOption {
  value: SensorMode
  preset: SensorModePreset
}

/**
 * 鼠标性能页能力：该型号对用户开放哪些回报率、传感器模式、按键扫描率。
 *
 * 只描述「开放什么」，不描述协议细节：回报率对应的传感器模式硬件槽位由
 * `mousePerfCfg.reportRateSlots` 按芯片提供，应用层不需要知道传感器型号。
 */
export interface MousePerfCapability {
  type: 'mousePerf'
  config: {
    /** 是否有性能页；false 或不声明该能力时不展示性能 Tab。 */
    enable: boolean
    /** 开放的回报率协议值，按界面展示顺序。 */
    reportRates: ReportRate[]
    /** 每个回报率下允许选择的传感器模式；未列出的回报率视为无可用模式。 */
    reportRateModes: Partial<Record<ReportRate, SensorMode[]>>
    /** 开放的传感器模式档位，按界面展示顺序。 */
    sensorModes: SensorModeOption[]
    /** 竞技档文案里的传感器扫描率标称值，如 '10K' / '13K'；无竞技档时不需要。 */
    sensorScanRateLabel?: string
    /** 开放的按键扫描率；不声明或空数组表示不展示按键扫描率卡片。 */
    keyScanRates?: KeyScanRate[]
    /** 通讯协议（多合一 / 1T1R）切换；不声明表示不开放。 */
    rfPacketType?: {
      /** 仅无线连接下可切换。 */
      wirelessOnly?: boolean
      /** 接收器主控最低版本，低于该版本不开放。 */
      minReceiverVersion?: string
    }
  }
}
/**
 * 宏能力：单条宏写入固件时的编码上限。
 * 标准：键盘 512、鼠标 4096、ESM 网吧鼠标 1024。
 */
export interface MacroCapability {
  type: 'macro'
  config: {
    /** 单条宏编码后的最大字节数（含 4 字节头部） */
    maxLength: number
  }
}

// 行程能力标识
export interface ItineraryCapability {
  type: 'itinerary'
  config: {
    /** 是否有行程功能 */
    hasTravel: boolean
    /**
     * 快速触发（RT）灵敏度精度选项表：UI 根据当前键位的精度切换位
     * （字节3 bit1-4，见 GcomeKeyTravel.precisionBits）匹配选项展示实际精度（mm）。
     * 按下精度 = 当前位 & 0x03（bit1=0x01 → 0.005mm，bit2=0x02 → 0.001mm，0 → 0.01mm）；
     * 释放精度 = 当前位 & 0x0c（bit3=0x04 → 0.005mm，bit4=0x08 → 0.001mm，0 → 0.01mm）。
     */
    rtPrecisionOptions?: Array<{
      /** 精度步进（mm），如 0.01 / 0.005 / 0.001 */
      step: number
      /** 按下 RT 对应置位的精度位（bit1-2） */
      pressBits: number
      /** 释放 RT 对应置位的精度位（bit3-4） */
      releaseBits: number
    }>
  }
}

// 键盘高级功能标识（DKS/MT/TGL/RS/SOCD）
export interface KeyFeaturesCapability {
  type: 'keyFeatures'
  config: {
    /** 是否支持「配置」改键 Tab（TabConfig）；未声明时由应用层按默认策略处理 */
    config?: boolean
    /** 是否支持动态键程（DKS） */
    dks?: boolean
    /** 是否支持按住/单击（MT） */
    mt?: boolean
    /** 是否支持切换开关（TGL） */
    tgl?: boolean
    /** 是否支持快速扳机（RS） */
    rs?: boolean
    /** 是否支持同时按键冲突解决（SOCD） */
    socd?: boolean
    /** 是否支持高级改键 */
    advanced?: boolean
  }
}

// 键盘性能配置
export interface KeyboardPerfCapability {
  type: 'keyboardPerf'
  config: {
    /** 是否启用独立的性能设置页签 */
    enablePerformanceConfig?: boolean
    /** 是否支持 MAC 模式开关 */
    macMode?: boolean
    /** 回报率可选项（字节值 → 标签映射） */
    reportRateOptions?: Array<{ value: number; label: string }>
    /** 休眠时间范围 */
    sleepTimeRange?: { min: number; max: number }
  }
}

// dpi步进范围
interface DpiStep {
  from: number
  to: number
  step: number
}

/**
 * 可选类型
 * 不选或default: 标准通讯接口，接收器Nordic更新也会走这里
 * update: 标准更新接口，例如大部分Nordic更新接口
 * updateWCH: 沁恒更新接口
 */
export type usageType = 'default' | 'update' | 'updateWCH' | 'pairing'
